import React, { useEffect, useState, type FC } from 'react'
import { type RouteComponentProps } from '@reach/router'
import { isNil } from 'lodash'
import { t } from 'i18next'
import { DeletePathwayModal } from '../../../components/DeletePathwayModal'
import { StopPathwayModal } from '../../../components/StopPathwayModal'
import { usePatientPathways } from '../../../hooks/usePatientPathways'
import { useClientPagination } from '../../../hooks/useClientPagination'
import { PathwayStatus } from '../../Pathway/types'
import { type PatientPathway } from '../types'
import { PatientPathwaysTabSkeleton } from './skeletons'
import { PatientPathwaysTable } from './PatientPathwaysTable'

interface PatientPathwaysTabProps {
  patientId: string
  patientName: string
}

export const PatientPathwaysTab: FC<RouteComponentProps<PatientPathwaysTabProps>> = ({ patientId, patientName }) => {
  const [selectedPathwayForDeletion, setSelectedPathwayForDeletion] = useState<PatientPathway>()
  const [openModalToDeletePathway, setOpenModalToDeletePathway] = useState<boolean>(false)

  const [selectedPathwayToStop, setSelectedPathwayToStop] = useState<PatientPathway>()
  const [openModalToStopPathway, setOpenModalToStopPathway] = useState<boolean>(false)

  const { refetch, patientPathways, loading } = usePatientPathways(patientId)

  useEffect(() => {
    if (!loading) {
      refetch()
    }
  }, [])

  const activePatientPathways =
    patientPathways?.filter(
      pathway => pathway.status === PathwayStatus.Active || pathway.status === PathwayStatus.Starting
    ) ?? []

  const completedPatientPathways = patientPathways?.filter(pathway => pathway.status === PathwayStatus.Completed) ?? []

  const stoppedPatientPathways = patientPathways?.filter(pathway => pathway.status === PathwayStatus.Stopped) ?? []

  // Client-side pagination for each table
  const activePagination = useClientPagination(activePatientPathways as PatientPathway[], { initialPerPage: 10 })
  const completedPagination = useClientPagination(completedPatientPathways as PatientPathway[], { initialPerPage: 10 })
  const stoppedPagination = useClientPagination(stoppedPatientPathways as PatientPathway[], { initialPerPage: 10 })

  const openDeleteConfirmationModal = (pathway: PatientPathway): void => {
    setSelectedPathwayForDeletion(pathway)
    setOpenModalToDeletePathway(true)
  }

  const openStopConfirmationModal = (pathway: PatientPathway): void => {
    setSelectedPathwayToStop(pathway)
    setOpenModalToStopPathway(true)
  }

  const handleStoppedModalClose = (): void => {
    setSelectedPathwayToStop(undefined)
    setOpenModalToStopPathway(false)
  }

  const handleDeleteModalClose = (): void => {
    setSelectedPathwayForDeletion(undefined)
    setOpenModalToDeletePathway(false)
  }

  if (loading && isNil(activePatientPathways)) {
    return <PatientPathwaysTabSkeleton />
  }

  return (
    <div className="flex flex-col gap-8 pb-8">
      <div className="flex flex-col gap-4">
        <div className="font-bold text-lg">{t('active_pathways')}</div>
        <PatientPathwaysTable
          pathways={activePagination.paginatedData}
          loading={loading}
          emptyMessage={t('no_pathway_found')}
          totalCount={activePagination.totalCount}
          currentPage={activePagination.currentPage}
          perPage={activePagination.perPage}
          onPageChange={activePagination.setPage}
          onDeletePathway={openDeleteConfirmationModal}
          onStopPathway={openStopConfirmationModal}
          tableType="active"
        />
      </div>

      <div className="flex flex-col gap-4">
        <div className="font-bold text-lg">{t('completed_pathways')}</div>
        <PatientPathwaysTable
          pathways={completedPagination.paginatedData}
          loading={loading}
          emptyMessage={t('no_pathway_found')}
          totalCount={completedPagination.totalCount}
          currentPage={completedPagination.currentPage}
          perPage={completedPagination.perPage}
          onPageChange={completedPagination.setPage}
          onDeletePathway={openDeleteConfirmationModal}
          tableType="completed"
        />
      </div>

      <div className="flex flex-col gap-4">
        <div className="font-bold text-lg">{t('stopped_pathways')}</div>
        <PatientPathwaysTable
          pathways={stoppedPagination.paginatedData}
          loading={loading}
          emptyMessage={t('no_pathway_found')}
          totalCount={stoppedPagination.totalCount}
          currentPage={stoppedPagination.currentPage}
          perPage={stoppedPagination.perPage}
          onPageChange={stoppedPagination.setPage}
          onDeletePathway={openDeleteConfirmationModal}
          tableType="stopped"
        />
      </div>

      {openModalToDeletePathway && (
        <DeletePathwayModal
          onClose={handleDeleteModalClose}
          careflow_id={selectedPathwayForDeletion?.id}
          patient_id={patientId}
          patientName={patientName}
          careflowName={selectedPathwayForDeletion?.title}
        />
      )}
      {openModalToStopPathway && (
        <StopPathwayModal
          onClose={handleStoppedModalClose}
          careflow_id={selectedPathwayToStop?.id}
          patientName={patientName}
          careflowName={selectedPathwayToStop?.title}
        />
      )}
    </div>
  )
}

PatientPathwaysTab.displayName = 'PatientPathwaysTab'
